/**
 * The slide-in help drawer. Two views:
 *   1. Index  — when no article is selected: search field + topic grid
 *   2. Article — when a slug is set: title, body (MarkdownLite), related,
 *                IMO refs as chips, sources, "Helpful?" feedback.
 *
 * Slides from the right on ≥640px, becomes a bottom-sheet at <640px.
 * Backdrop closes on click. Esc closes (handled by HelpProvider).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Search, ChevronRight, ChevronLeft, ThumbsUp, ThumbsDown,
  ArrowRight, BookOpen, History, Sparkles, ClipboardList, Ship, Camera,
  PenLine, Shield, Award, Wand2, AlertTriangle, Scale,
} from 'lucide-react';
import {
  ARTICLES_BY_SLUG, TOPIC_META, articlesByTopic,
  searchArticles, type HelpArticle, type HelpTopic,
} from './articles';
import { useHelp } from './HelpProvider';
import MarkdownLite from './MarkdownLite';

const TOPIC_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles, ClipboardList, Ship, Camera, PenLine, Shield, Award, BookOpen,
  Wand2, AlertTriangle, Scale,
};

function TopicIcon({ name, className }: { name: string; className?: string }) {
  const C = TOPIC_ICONS[name] ?? BookOpen;
  return <C className={className} />;
}

const FEEDBACK_KEY = 'varuna-help-feedback';

function readFeedback(): Record<string, 'up' | 'down'> {
  try { return JSON.parse(localStorage.getItem(FEEDBACK_KEY) ?? '{}'); }
  catch { return {}; }
}
function writeFeedback(slug: string, value: 'up' | 'down') {
  try {
    const all = readFeedback();
    all[slug] = value;
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

export default function HelpDrawer() {
  const { open, slug, recent, closeHelp, openHelp, markViewed } = useHelp();
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Reset state when drawer opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      // Auto-focus search after a tick (post-animation)
      const id = setTimeout(() => searchRef.current?.focus(), 120);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Mark current slug as viewed
  useEffect(() => {
    if (open && slug) markViewed(slug);
  }, [open, slug, markViewed]);

  const article: HelpArticle | null = slug ? ARTICLES_BY_SLUG[slug] ?? null : null;

  const searchResults = useMemo(() => searchArticles(query), [query]);

  // Keyboard nav inside drawer
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!open) return;
      if (e.key === '/' && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'ArrowDown' && searchResults.length > 0) {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, searchResults.length - 1));
      } else if (e.key === 'ArrowUp' && searchResults.length > 0) {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === 'Enter' && searchResults[highlight]) {
        e.preventDefault();
        openHelp(searchResults[highlight].slug);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, searchResults, highlight, openHelp]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeHelp}
        aria-hidden={!open}
        className={`fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      {/* Panel */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Help"
        aria-hidden={!open}
        className={`fixed z-50 bg-slate-950 border border-slate-800 shadow-2xl flex flex-col
          inset-x-0 bottom-0 h-[90vh] rounded-t-2xl
          sm:inset-y-0 sm:right-0 sm:left-auto sm:h-full sm:w-[min(560px,90vw)] sm:rounded-none sm:rounded-l-2xl
          transition-transform duration-200 ease-out
          ${open
            ? 'translate-y-0 sm:translate-x-0'
            : 'translate-y-full sm:translate-y-0 sm:translate-x-full'}
        `}
      >
        {/* Sticky header */}
        <header className="shrink-0 px-4 py-3 border-b border-slate-800 bg-slate-950/95 backdrop-blur sticky top-0">
          <div className="flex items-center gap-2">
            {article && (
              <button
                onClick={() => openHelp(null)}
                aria-label="Back to index"
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all duration-150 active:scale-[0.96]"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search help…  (try: observation, MARPOL, offline)"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
                className="w-full pl-8 pr-2 py-2 rounded-lg bg-slate-900 border border-slate-800 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-400 font-mono border border-slate-700">Esc</kbd>
            <button
              onClick={closeHelp}
              aria-label="Close help"
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all duration-150 active:scale-[0.96]"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Search results override everything */}
          {query.trim() && (
            <SearchResultsView
              results={searchResults}
              highlight={highlight}
              onPick={(s) => openHelp(s)}
              query={query}
            />
          )}

          {!query.trim() && article && (
            <ArticleView
              article={article}
              onPick={(s) => openHelp(s)}
            />
          )}

          {!query.trim() && !article && (
            <IndexView
              recent={recent}
              onPick={(s) => openHelp(s)}
            />
          )}
        </div>

        {/* Footer with shortcuts */}
        <footer className="shrink-0 px-4 py-2 border-t border-slate-800 bg-slate-950/95 text-[11px] text-slate-500 flex items-center justify-between">
          <span>Press <kbd className="px-1 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">/</kbd> to search · <kbd className="px-1 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">?</kbd> to toggle</span>
          <a href="/help" className="text-cyan-400 hover:text-cyan-300">Open full library →</a>
        </footer>
      </aside>
    </>
  );
}

// ─── Index view ───────────────────────────────────────────────────────────────

function IndexView({ recent, onPick }: { recent: string[]; onPick: (slug: string) => void }) {
  const recentArticles = recent.map((s) => ARTICLES_BY_SLUG[s]).filter(Boolean);
  const topics = Object.keys(TOPIC_META) as HelpTopic[];

  return (
    <div className="p-4 space-y-5">
      {recentArticles.length > 0 && (
        <section>
          <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider text-cyan-400 font-semibold">
            <History className="w-3 h-3" />
            <span>Recently viewed</span>
          </div>
          <ul className="space-y-1">
            {recentArticles.map((a) => (
              <li key={a.slug}>
                <button
                  onClick={() => onPick(a.slug)}
                  className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-slate-900 transition-all duration-150 active:scale-[0.99] text-left group"
                >
                  <span className="text-sm text-slate-200 truncate">{a.title}</span>
                  <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold mb-2">Browse by topic</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {topics.map((t) => {
            const meta = TOPIC_META[t];
            const count = articlesByTopic(t).length;
            return (
              <button
                key={t}
                onClick={() => {
                  // Show first article in topic
                  const first = articlesByTopic(t)[0];
                  if (first) onPick(first.slug);
                }}
                disabled={count === 0}
                className="text-left p-3 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-cyan-700/60 hover:bg-slate-900 transition-all duration-150 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="flex items-start gap-2.5">
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-700/40 flex items-center justify-center text-cyan-300 group-hover:from-cyan-500/30 group-hover:to-blue-600/30 transition-all">
                    <TopicIcon name={meta.icon} className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white">{meta.label}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{meta.description}</div>
                    <div className="text-[10px] text-cyan-400/70 mt-1">{count} {count === 1 ? 'article' : 'articles'}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ─── Search results view ─────────────────────────────────────────────────────

function SearchResultsView({
  results, highlight, onPick, query,
}: {
  results: HelpArticle[]; highlight: number; onPick: (slug: string) => void; query: string;
}) {
  if (results.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-sm text-slate-300">No articles match <span className="text-cyan-300">"{query}"</span>.</div>
        <div className="text-xs text-slate-500 mt-1">Try: observation, MARPOL, offline, smart help, AI draft</div>
      </div>
    );
  }
  return (
    <div className="p-3 space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold px-2 mb-1">
        {results.length} {results.length === 1 ? 'result' : 'results'}
      </div>
      {results.map((a, idx) => (
        <button
          key={a.slug}
          onClick={() => onPick(a.slug)}
          className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-100 ${
            idx === highlight ? 'bg-cyan-900/30 border border-cyan-700/50' : 'bg-slate-900/40 border border-transparent hover:bg-slate-900 hover:border-slate-700'
          }`}
        >
          <div className="text-[10px] uppercase tracking-wider text-cyan-400/80">{TOPIC_META[a.topic].label}</div>
          <div className="text-sm font-semibold text-white">{a.title}</div>
          <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{a.summary}</div>
        </button>
      ))}
    </div>
  );
}

// ─── Article view ────────────────────────────────────────────────────────────

function ArticleView({ article, onPick }: { article: HelpArticle; onPick: (slug: string) => void }) {
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    const all = readFeedback();
    setFeedback(all[article.slug] ?? null);
  }, [article.slug]);

  function vote(v: 'up' | 'down') {
    writeFeedback(article.slug, v);
    setFeedback(v);
  }

  const meta = TOPIC_META[article.topic];

  return (
    <article className="p-4 pb-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
        <BookOpen className="w-3 h-3 text-cyan-400" />
        <span className="text-cyan-400">Help</span>
        <ChevronRight className="w-3 h-3" />
        <span>{meta.label}</span>
      </div>

      {/* Title */}
      <h1 className="text-xl font-bold text-white">{article.title}</h1>
      <p className="text-sm text-slate-400 mt-1">{article.summary}</p>

      {/* Body */}
      <MarkdownLite source={article.body} className="text-[14px] mt-4" />

      {/* IMO refs */}
      {article.imoRefs && article.imoRefs.length > 0 && (
        <section className="mt-5 pt-4 border-t border-slate-800">
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold mb-2">Regulation references</div>
          <div className="flex flex-wrap gap-1.5">
            {article.imoRefs.map((r) => (
              <span key={r} className="px-2 py-0.5 rounded-full bg-slate-800/80 border border-slate-700 text-[11px] text-slate-200">
                {r}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Sources */}
      {article.sources && article.sources.length > 0 && (
        <section className="mt-5 pt-4 border-t border-slate-800">
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold mb-2">Sources</div>
          <ul className="space-y-1">
            {article.sources.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                >
                  {s.label} ↗
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Related */}
      {article.related && article.related.length > 0 && (
        <section className="mt-5 pt-4 border-t border-slate-800">
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold mb-2">Related</div>
          <ul className="space-y-1">
            {article.related.map((s) => {
              const r = ARTICLES_BY_SLUG[s];
              if (!r) return null;
              return (
                <li key={s}>
                  <button
                    onClick={() => onPick(s)}
                    className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md bg-slate-900/40 hover:bg-slate-900 border border-slate-800 hover:border-slate-700 transition-all duration-150 active:scale-[0.99] text-left group"
                  >
                    <span className="text-sm text-slate-200">{r.title}</span>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Feedback */}
      <section className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-400">Was this helpful?</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => vote('up')}
            aria-label="Helpful"
            className={`p-1.5 rounded-lg border transition-all duration-150 active:scale-[0.96] ${
              feedback === 'up'
                ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-emerald-300 hover:border-emerald-700/50'
            }`}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => vote('down')}
            aria-label="Not helpful"
            className={`p-1.5 rounded-lg border transition-all duration-150 active:scale-[0.96] ${
              feedback === 'down'
                ? 'bg-rose-500/20 border-rose-500/50 text-rose-300'
                : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-rose-300 hover:border-rose-700/50'
            }`}
          >
            <ThumbsDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>
    </article>
  );
}
